import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle,
  Search,
  RefreshCw,
  ShieldCheck,
  Power,
  PowerOff,
  Sparkles,
  Loader2,
  Wand2,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

// Procurement Intelligence — Phase 1 opt-in cockpit.
// Per-supplier and per-product activation. Nothing enters the cockpit
// unless explicitly enabled here.

const CADENCE_OPTIONS = [
  { value: 'weekly', label: 'Semanal' },
  { value: 'biweekly', label: 'Quinzenal' },
  { value: 'monthly', label: 'Mensal' },
  { value: 'on_demand', label: 'Sob encomenda' },
];

const ProcurementConfig = () => {
  const [scope, setScope] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Edit dialog
  const [editingCode, setEditingCode] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Activation candidates dialog
  const [candidatesCode, setCandidatesCode] = useState(null);
  const [candidatesData, setCandidatesData] = useState(null);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [selectedSkus, setSelectedSkus] = useState(new Set());
  const [activatingSelection, setActivatingSelection] = useState(false);
  // Phase 4 — SAGE import inside candidates dialog
  const [sagePreview, setSagePreview] = useState(null);
  const [sageApplying, setSageApplying] = useState(false);

  const loadAll = async () => {
    setRefreshing(true);
    try {
      const [scopeRes, configsRes] = await Promise.all([
        axios.get(`${API}/procurement-config/scope-summary`),
        axios.get(`${API}/procurement-config/suppliers`),
      ]);
      setScope(scopeRes.data);
      setSuppliers(configsRes.data?.configs || []);
    } catch (err) {
      toast.error('Falha ao carregar configurações de procurement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return suppliers.filter((s) => {
      if (statusFilter === 'enabled' && !s.procurement_intelligence_enabled) return false;
      if (statusFilter === 'disabled' && s.procurement_intelligence_enabled) return false;
      if (!term) return true;
      const hay = `${s.supplier_name || ''} ${s.sage_supplier_code || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [suppliers, search, statusFilter]);

  const openEdit = async (code, supplierName) => {
    try {
      const res = await axios.get(
        `${API}/procurement-config/suppliers/${encodeURIComponent(code)}`,
      );
      const cfg = res.data.config || {};
      setEditForm({
        sage_supplier_code: code,
        supplier_name: supplierName || res.data.supplier_name || '',
        procurement_intelligence_enabled: !!cfg.procurement_intelligence_enabled,
        generate_procurement_cards: !!cfg.generate_procurement_cards,
        allow_auto_suggestions: !!cfg.allow_auto_suggestions,
        lead_time_days_avg: cfg.lead_time_days_avg ?? '',
        global_moq: cfg.global_moq ?? '',
        min_order_value: cfg.min_order_value ?? '',
        free_shipping_value: cfg.free_shipping_value ?? '',
        order_cadence: cfg.order_cadence ?? '',
        is_strategic: !!cfg.is_strategic,
        is_slow: !!cfg.is_slow,
        under_monitoring: !!cfg.under_monitoring,
        is_preferred: !!cfg.is_preferred,
        reliability_score: cfg.reliability_score ?? '',
        procurement_notes: cfg.procurement_notes ?? '',
        product_count: res.data.product_count ?? 0,
        tracking_count: res.data.tracking_count ?? 0,
      });
      setEditingCode(code);
    } catch {
      toast.error('Falha ao carregar config do fornecedor');
    }
  };

  const closeEdit = () => {
    setEditingCode(null);
    setEditForm(null);
  };

  const toNumOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const saveEdit = async () => {
    if (!editForm) return;
    setSavingEdit(true);
    const payload = {
      supplier_name: editForm.supplier_name || null,
      procurement_intelligence_enabled: editForm.procurement_intelligence_enabled,
      generate_procurement_cards: editForm.generate_procurement_cards,
      allow_auto_suggestions: editForm.allow_auto_suggestions,
      lead_time_days_avg: toNumOrNull(editForm.lead_time_days_avg),
      global_moq: toNumOrNull(editForm.global_moq),
      min_order_value: toNumOrNull(editForm.min_order_value),
      free_shipping_value: toNumOrNull(editForm.free_shipping_value),
      order_cadence: editForm.order_cadence || null,
      is_strategic: editForm.is_strategic,
      is_slow: editForm.is_slow,
      under_monitoring: editForm.under_monitoring,
      is_preferred: editForm.is_preferred,
      reliability_score: toNumOrNull(editForm.reliability_score),
      procurement_notes: editForm.procurement_notes || null,
    };
    try {
      await axios.put(
        `${API}/procurement-config/suppliers/${encodeURIComponent(editingCode)}`,
        payload,
      );
      toast.success('Configuração de fornecedor guardada');
      closeEdit();
      await loadAll();
    } catch {
      toast.error('Falha ao guardar configuração');
    } finally {
      setSavingEdit(false);
    }
  };

  const bulkActivate = async (code, enable) => {
    if (!window.confirm(
      enable
        ? `Activar AI tracking em TODOS os SKUs do fornecedor ${code}?`
        : `Desactivar AI tracking em TODOS os SKUs do fornecedor ${code}?`,
    )) return;
    setBulkLoading(true);
    try {
      const res = await axios.post(
        `${API}/procurement-config/bulk/activate-by-supplier`,
        {
          sage_supplier_code: code,
          enable_ai_tracking: enable,
          allow_alerts: enable,
          allow_forecast: enable,
        },
      );
      toast.success(
        `${enable ? 'Activados' : 'Desactivados'}: ${res.data.modified_count} SKUs`,
      );
      await loadAll();
    } catch {
      toast.error('Falha na activação em massa');
    } finally {
      setBulkLoading(false);
    }
  };

  const openCandidates = async (code) => {
    setCandidatesCode(code);
    setCandidatesData(null);
    setSagePreview(null);
    setSelectedSkus(new Set());
    setCandidatesLoading(true);
    try {
      const [candRes, sageRes] = await Promise.all([
        axios.get(`${API}/procurement-config/suppliers/${encodeURIComponent(code)}/activation-candidates`),
        axios.get(`${API}/procurement-config/sage-import/preview?supplier_code=${encodeURIComponent(code)}`),
      ]);
      setCandidatesData(candRes.data);
      setSagePreview(sageRes.data);
    } catch {
      toast.error('Falha ao calcular candidatos de activação');
      setCandidatesCode(null);
    } finally {
      setCandidatesLoading(false);
    }
  };

  const closeCandidates = () => {
    setCandidatesCode(null);
    setCandidatesData(null);
    setSagePreview(null);
    setSelectedSkus(new Set());
  };

  const applySageImport = async () => {
    if (!candidatesCode) return;
    if (!window.confirm(
      `Importar dados do SAGE (referência fornecedor, descrição, lead time se existir, preço se existir) para todas as ${sagePreview?.rows_with_signal || 0} relações do fornecedor ${candidatesCode}?`,
    )) return;
    setSageApplying(true);
    try {
      const res = await axios.post(`${API}/procurement-config/sage-import/apply`, {
        supplier_code: candidatesCode,
        confirm: true,
      });
      toast.success(`SAGE: ${res.data.created} criados, ${res.data.updated} actualizados`);
      // Refresh preview to reflect new state
      const sageRes = await axios.get(`${API}/procurement-config/sage-import/preview?supplier_code=${encodeURIComponent(candidatesCode)}`);
      setSagePreview(sageRes.data);
    } catch {
      toast.error('Falha ao importar dados do SAGE');
    } finally {
      setSageApplying(false);
    }
  };

  const toggleSelected = (sku) => {
    setSelectedSkus((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  const selectAllCandidates = () => {
    if (!candidatesData?.candidates) return;
    setSelectedSkus(new Set(candidatesData.candidates.map((c) => c.sku)));
  };

  const activateSelected = async () => {
    if (selectedSkus.size === 0) return;
    setActivatingSelection(true);
    try {
      const res = await axios.post(`${API}/procurement-config/bulk/activate-by-skus`, {
        skus: Array.from(selectedSkus),
        enable_ai_tracking: true,
        allow_alerts: true,
        allow_forecast: true,
      });
      toast.success(`${res.data.modified_count} SKUs activados`);
      closeCandidates();
      await loadAll();
    } catch {
      toast.error('Falha ao activar selecção');
    } finally {
      setActivatingSelection(false);
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center min-h-[60vh]"
        data-testid="procurement-config-loading"
      >
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-700" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="procurement-config-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Sparkles className="h-5 w-5 md:h-6 md:w-6 text-violet-600" />
                Procurement Intelligence
              </h1>
              <p className="text-sm text-slate-500 mt-1 max-w-2xl">
                Activa fornecedores para o cockpit de procurement. Apenas SKUs com AI tracking activo + supplier activado geram alertas e sugestões.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={loadAll}
              disabled={refreshing}
              data-testid="procurement-config-refresh"
              className="shrink-0"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Recarregar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-6">
        {/* Scope summary */}
      {scope && (
        <Card data-testid="scope-summary">
          <CardContent className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
              <div>
                <p className="text-xs uppercase text-slate-500">Produtos no catálogo</p>
                <p className="text-2xl font-semibold text-slate-900 mt-1 font-mono">
                  {scope.products_total.toLocaleString('pt-PT')}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">SKUs com AI tracking</p>
                <p className="text-2xl font-semibold text-emerald-700 mt-1 font-mono">
                  {scope.products_ai_tracking_enabled.toLocaleString('pt-PT')}
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">SKUs no cockpit</p>
                <p className="text-2xl font-semibold text-slate-900 mt-1 font-mono">
                  {scope.products_in_scope.toLocaleString('pt-PT')}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  tracking + supplier activo
                </p>
              </div>
              <div>
                <p className="text-xs uppercase text-slate-500">Fornecedores activos</p>
                <p className="text-2xl font-semibold text-slate-900 mt-1 font-mono">
                  {scope.suppliers_pi_enabled} / {scope.suppliers_total}
                </p>
              </div>
            </div>
            {scope.products_in_scope === 0 && (
              <div className="mt-4 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Cockpit vazio: nenhum SKU está no scope de procurement.
                  Activa primeiro um fornecedor abaixo e depois usa
                  "Activar todos os SKUs deste fornecedor".
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters + table */}
      <Card data-testid="suppliers-table-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span>Fornecedores ({filtered.length} de {suppliers.length})</span>
          </CardTitle>
          <div className="flex flex-wrap gap-3 pt-2">
            <div className="relative flex-1 min-w-[260px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Procurar por nome ou código SAGE..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="supplier-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]" data-testid="supplier-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="enabled">Activos no cockpit</SelectItem>
                <SelectItem value="disabled">Desactivados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código SAGE</TableHead>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead className="text-right">SKUs</TableHead>
                  <TableHead className="text-right">No tracking</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acções</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => {
                  const code = s.sage_supplier_code;
                  const enabled = !!s.procurement_intelligence_enabled;
                  const cards = !!s.generate_procurement_cards;
                  const tracking = s.tracking_count ?? 0;
                  return (
                    <TableRow key={code} data-testid={`supplier-row-${code}`}>
                      <TableCell className="font-mono text-xs">{code}</TableCell>
                      <TableCell className="text-slate-800 max-w-md truncate">
                        {s.supplier_name}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {s.product_count?.toLocaleString?.('pt-PT') ?? '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {tracking.toLocaleString?.('pt-PT')}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {enabled ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 hover:bg-emerald-100">
                              PI activa
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-500">
                              PI desactivada
                            </Badge>
                          )}
                          {cards && (
                            <Badge variant="outline" className="text-xs">
                              cards
                            </Badge>
                          )}
                          {s.is_strategic && (
                            <Badge variant="outline" className="text-xs bg-blue-50">
                              estratégico
                            </Badge>
                          )}
                          {s.is_slow && (
                            <Badge variant="outline" className="text-xs bg-amber-50">
                              lento
                            </Badge>
                          )}
                          {s.under_monitoring && (
                            <Badge variant="outline" className="text-xs bg-orange-50">
                              monitorizado
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right space-x-2 whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openCandidates(code)}
                          data-testid={`supplier-suggest-btn-${code}`}
                          title="Sugerir SKUs operacionalmente relevantes"
                        >
                          <Wand2 className="h-3 w-3 mr-1" />
                          Sugerir
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(code, s.supplier_name)}
                          data-testid={`supplier-config-btn-${code}`}
                        >
                          Configurar
                        </Button>
                        {enabled && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={bulkLoading}
                            onClick={() => bulkActivate(code, true)}
                            data-testid={`supplier-bulk-on-${code}`}
                          >
                            <Power className="h-3 w-3 mr-1" />
                            Activar SKUs
                          </Button>
                        )}
                        {tracking > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={bulkLoading}
                            onClick={() => bulkActivate(code, false)}
                            data-testid={`supplier-bulk-off-${code}`}
                            className="text-red-700"
                          >
                            <PowerOff className="h-3 w-3 mr-1" />
                            Desactivar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      Nenhum fornecedor corresponde aos filtros.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editingCode} onOpenChange={(open) => !open && closeEdit()}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="supplier-edit-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-slate-600" />
              {editForm?.supplier_name || editingCode}
            </DialogTitle>
            <DialogDescription>
              Configuração de Procurement Intelligence. {editForm?.product_count} SKUs ligados ·
              {' '}{editForm?.tracking_count} em tracking.
            </DialogDescription>
          </DialogHeader>

          {editForm && (
            <div className="space-y-5 pt-2">
              {/* Master gates */}
              <div className="rounded border border-slate-200 p-4 bg-slate-50/50 space-y-3">
                <h4 className="text-sm font-semibold text-slate-700">Gates principais</h4>
                {[
                  { key: 'procurement_intelligence_enabled', label: 'Procurement Intelligence activa', hint: 'Gate principal. Sem isto, fornecedor invisível ao cockpit.' },
                  { key: 'generate_procurement_cards', label: 'Gerar procurement cards', hint: 'Mesmo com PI activa, os cards só aparecem se isto estiver ON.' },
                  { key: 'allow_auto_suggestions', label: 'Permitir sugestões automáticas', hint: 'Desligado por defeito — sugestões só com revisão humana.' },
                ].map(({ key, label, hint }) => (
                  <div key={key} className="flex items-start justify-between gap-3">
                    <div>
                      <Label htmlFor={key} className="text-sm">{label}</Label>
                      <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
                    </div>
                    <Switch
                      id={key}
                      data-testid={`switch-${key}`}
                      checked={!!editForm[key]}
                      onCheckedChange={(v) => setEditForm({ ...editForm, [key]: v })}
                    />
                  </div>
                ))}
              </div>

              {/* Operational params */}
              <div className="rounded border border-slate-200 p-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-700">Parâmetros operacionais</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="lead_time" className="text-xs">Lead time médio (dias)</Label>
                    <Input
                      id="lead_time"
                      data-testid="input-lead-time"
                      type="number"
                      value={editForm.lead_time_days_avg}
                      onChange={(e) => setEditForm({ ...editForm, lead_time_days_avg: e.target.value })}
                      placeholder="—"
                    />
                  </div>
                  <div>
                    <Label htmlFor="moq" className="text-xs">MOQ global</Label>
                    <Input
                      id="moq"
                      data-testid="input-global-moq"
                      type="number"
                      value={editForm.global_moq}
                      onChange={(e) => setEditForm({ ...editForm, global_moq: e.target.value })}
                      placeholder="—"
                    />
                  </div>
                  <div>
                    <Label htmlFor="min_value" className="text-xs">Valor mínimo encomenda (€)</Label>
                    <Input
                      id="min_value"
                      data-testid="input-min-order-value"
                      type="number"
                      step="0.01"
                      value={editForm.min_order_value}
                      onChange={(e) => setEditForm({ ...editForm, min_order_value: e.target.value })}
                      placeholder="—"
                    />
                  </div>
                  <div>
                    <Label htmlFor="free_ship" className="text-xs">Valor portes grátis (€)</Label>
                    <Input
                      id="free_ship"
                      data-testid="input-free-shipping"
                      type="number"
                      step="0.01"
                      value={editForm.free_shipping_value}
                      onChange={(e) => setEditForm({ ...editForm, free_shipping_value: e.target.value })}
                      placeholder="—"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cadence" className="text-xs">Cadência habitual</Label>
                    <Select
                      value={editForm.order_cadence || ''}
                      onValueChange={(v) => setEditForm({ ...editForm, order_cadence: v })}
                    >
                      <SelectTrigger data-testid="select-order-cadence">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {CADENCE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="reliability" className="text-xs">Fiabilidade (1-5)</Label>
                    <Input
                      id="reliability"
                      data-testid="input-reliability"
                      type="number"
                      min="1"
                      max="5"
                      value={editForm.reliability_score}
                      onChange={(e) => setEditForm({ ...editForm, reliability_score: e.target.value })}
                      placeholder="—"
                    />
                  </div>
                </div>
              </div>

              {/* Flags */}
              <div className="rounded border border-slate-200 p-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-700">Classificação</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'is_preferred', label: 'Fornecedor preferencial' },
                    { key: 'is_strategic', label: 'Estratégico' },
                    { key: 'is_slow', label: 'Lento' },
                    { key: 'under_monitoring', label: 'Sob monitorização' },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2">
                      <Switch
                        id={key}
                        data-testid={`switch-${key}`}
                        checked={!!editForm[key]}
                        onCheckedChange={(v) => setEditForm({ ...editForm, [key]: v })}
                      />
                      <Label htmlFor={key} className="text-sm">{label}</Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes" className="text-xs">Notas procurement</Label>
                <Textarea
                  id="notes"
                  data-testid="input-procurement-notes"
                  rows={2}
                  value={editForm.procurement_notes}
                  onChange={(e) => setEditForm({ ...editForm, procurement_notes: e.target.value })}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeEdit} disabled={savingEdit}>
              Cancelar
            </Button>
            <Button
              onClick={saveEdit}
              disabled={savingEdit}
              data-testid="supplier-save-btn"
            >
              {savingEdit && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activation candidates dialog */}
      <Dialog open={!!candidatesCode} onOpenChange={(open) => !open && closeCandidates()}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="candidates-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-slate-600" />
              SKUs sugeridos para activar
            </DialogTitle>
            <DialogDescription>
              Top SKUs deste fornecedor com sinais reais nos últimos 90 dias
              (vendas, consumo técnico, rupturas, compromissos cliente).
              Conservador — a decisão final é sua.
            </DialogDescription>
          </DialogHeader>

          {candidatesLoading && (
            <div className="flex items-center justify-center py-10" data-testid="candidates-loading">
              <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
            </div>
          )}

          {!candidatesLoading && candidatesData && (
            <div className="space-y-4">
              {/* SAGE DadosCompra panel */}
              {sagePreview && (
                <div className="rounded border border-slate-200 bg-slate-50 p-4" data-testid="sage-import-panel">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        Dados SAGE para este fornecedor
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {sagePreview.total_relationships} relações conhecidas ·
                        {' '}{sagePreview.existing_overrides} já importadas
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className={
                        sagePreview.recommendation?.verdict === 'GO'
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                          : sagePreview.recommendation?.verdict === 'PARTIAL'
                          ? 'bg-amber-50 text-amber-800 border-amber-300'
                          : 'bg-slate-100 text-slate-700'
                      }
                    >
                      {sagePreview.recommendation?.verdict || '—'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
                    {Object.entries(sagePreview.coverage).map(([field, info]) => (
                      <div key={field}>
                        <p className="text-slate-500 uppercase text-[10px]">{field}</p>
                        <p className="font-mono text-slate-800">
                          {info.count?.toLocaleString?.('pt-PT')} ({info.pct}%)
                        </p>
                      </div>
                    ))}
                  </div>
                  {sagePreview.recommendation?.summary && (
                    <p className="text-xs text-slate-600 mt-3 italic">
                      {sagePreview.recommendation.summary}
                    </p>
                  )}
                  {sagePreview.rows_to_create + sagePreview.rows_to_update > 0 && (
                    <div className="mt-3 flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={applySageImport}
                        disabled={sageApplying}
                        data-testid="sage-import-apply-btn"
                      >
                        {sageApplying && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
                        Importar do SAGE ({sagePreview.rows_to_create} novos ·
                        {' '}{sagePreview.rows_to_update} actualizados)
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="text-xs text-slate-500 flex flex-wrap gap-3">
                <span>{candidatesData.evaluated} SKUs avaliados</span>
                <span>·</span>
                <span>{candidatesData.above_threshold ?? candidatesData.candidates.length} acima do limite (score ≥ {candidatesData.min_score})</span>
                <span>·</span>
                <span>janela {candidatesData.lookback_days}d</span>
              </div>

              {candidatesData.candidates.length === 0 ? (
                <div className="rounded border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-600" data-testid="candidates-empty">
                  <CheckCircle2 className="h-6 w-6 text-slate-400 mx-auto mb-2" />
                  Sem candidatos relevantes neste fornecedor nos últimos 90 dias.
                  <p className="text-xs text-slate-500 mt-2">
                    Honestidade operacional: sem sinais reais não há sugestões.
                    Considere activar manualmente um SKU se souber que vai
                    precisar dele.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-center">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={selectAllCandidates}
                      data-testid="candidates-select-all"
                    >
                      Seleccionar todos ({candidatesData.candidates.length})
                    </Button>
                    <span className="text-xs text-slate-500">
                      {selectedSkus.size} seleccionado(s)
                    </span>
                  </div>

                  <div className="space-y-2">
                    {candidatesData.candidates.map((c) => {
                      const checked = selectedSkus.has(c.sku);
                      return (
                        <div
                          key={c.sku}
                          className={`rounded border p-3 transition-colors ${
                            checked ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'
                          }`}
                          data-testid={`candidate-${c.sku}`}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              className="mt-1 h-4 w-4"
                              checked={checked}
                              onChange={() => toggleSelected(c.sku)}
                              data-testid={`candidate-checkbox-${c.sku}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="font-medium text-slate-800 truncate">
                                    {c.name}
                                  </p>
                                  <p className="text-xs text-slate-500 font-mono">{c.sku}</p>
                                </div>
                                <div className="text-right shrink-0">
                                  <Badge variant="outline" className="bg-slate-50">
                                    score {c.score}
                                  </Badge>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {c.reasons.map((r) => (
                                  <Badge key={r} className="bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-50 text-xs">
                                    {r}
                                  </Badge>
                                ))}
                              </div>
                              <div className="text-xs text-slate-500 mt-2 flex flex-wrap gap-x-4 gap-y-0.5 font-mono">
                                <span>stock: {c.current_stock}</span>
                                {c.signals.sale_events_90d > 0 && (
                                  <span>vendas 90d: {c.signals.sale_events_90d}× ({c.signals.sale_qty_90d}un)</span>
                                )}
                                {c.signals.tech_events_90d > 0 && (
                                  <span>consumo téc 90d: {c.signals.tech_events_90d}</span>
                                )}
                                {c.signals.critical_alerts_90d > 0 && (
                                  <span>rupturas 90d: {c.signals.critical_alerts_90d}</span>
                                )}
                                {c.signals.client_commitments > 0 && (
                                  <span>compromissos cliente: {c.signals.client_commitments}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={closeCandidates}>
              Fechar
            </Button>
            {candidatesData && candidatesData.candidates.length > 0 && (
              <Button
                onClick={activateSelected}
                disabled={selectedSkus.size === 0 || activatingSelection}
                data-testid="candidates-activate-btn"
              >
                {activatingSelection && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Activar seleccionados ({selectedSkus.size})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default ProcurementConfig;
